import React, { FC, memo, useState, useCallback, useMemo } from 'react'

import { Field, Formik } from 'formik'

import { Dialog } from '@ucheba/ui/components/Dialog/desktop'

import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { TextArea } from '@ucheba/ui/components/TextArea/desktop'
import { Button } from '@ucheba/ui/components/Button/desktop'
import { Checkbox } from '@ucheba/ui/components/Checkbox/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'

import { ICommunicationFormProps, EFormType } from '../../types'
import { useCommunicationForm } from '../../bll'
import { FORM_TYPES } from '../../constants'

import FeedbackHeader from './FeedbackHeader'

const CommunicationForm: FC<ICommunicationFormProps> = ({
  type = 'institution',
  formType,
  isOpen,
  parentId,
  onClose: onCloseFromProps,
  scrollTo,
  list,
  rootId,
  offset,
  limit,
  onAfterSubmit,
}) => {
  const [sendingLoading, setSendingLoading] = useState(false)
  const [rate, setRate] = useState<number>(0)

  const [text, setText] = useState('')
  const [isPublished, setIsPublished] = useState(true)

  const { onSubmit, error, setError } = useCommunicationForm(
    formType,
    list,
    type,
    () => {
      setSendingLoading(false)
      onCloseFromProps()
      setText('')
      setIsPublished(true)
      setRate(0)
      setError(null)
      if (scrollTo) scrollTo()
    },
    limit,
    offset,
    onAfterSubmit
  )

  const handleTextareaChange = useCallback((value: string): void => {
    setText(value)
  }, [])

  const handleIsPublishedClick = useCallback((): void => {
    setIsPublished(!isPublished)
  }, [isPublished])

  const handleSubmitClick = useCallback((): void => {
    setSendingLoading(true)

    onSubmit(text, isPublished, parentId, rate, rootId)
  }, [text, rate, isPublished, parentId, onSubmit, rootId])

  /* Локальный onClose здесь для того, чтобы reset-нуть на исходное состояние полей */
  const onClose = useCallback((): void => {
    onCloseFromProps()
    setText('')
    setIsPublished(true)
    setRate(0)
    setError(null)
    setSendingLoading(false)
  }, [onCloseFromProps, setError])

  const isSendButtonDisabled = useMemo(
    () =>
      !text ||
      sendingLoading ||
      ((rate === 0 || !text) && formType === EFormType.feedbacks),
    [formType, rate, sendingLoading, text]
  )

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} spacing='xlarge' overflow='auto' onClose={onClose}>
      <Heading h2={FORM_TYPES[formType]} spacing='large' />

      {formType === EFormType.feedbacks && (
        <FeedbackHeader sendingLoading={sendingLoading} rate={rate} setRate={setRate} />
      )}

      {/*
        Сделал попроще, без филдов и прочего
        Использовал <Formik initialValues={{ isPublished: true }}> только для того, чтобы заработал чекбокс
      */}
      <Formik onSubmit={() => undefined} initialValues={{ isPublished: true }}>
        <>
          <TextArea
            disabled={sendingLoading}
            label={FORM_TYPES[formType]}
            name='text'
            error={error}
            onChange={handleTextareaChange}
          />

          <Spacing spacing='large' />

          <Grid justifyContent='space-between' spacing='small' alignItems='center' nowrap>
            <GridCol width='130px'>
              <Button
                color='blue'
                block
                type='submit'
                disabled={isSendButtonDisabled}
                progress={sendingLoading}
                onClick={handleSubmitClick}
              >
                Отправить
              </Button>
            </GridCol>

            {formType !== EFormType.feedbacks && (
              <GridCol width='auto'>
                <Field
                  type='checkbox'
                  name='isPublished'
                  as={Checkbox}
                  checked={isPublished}
                  onChange={handleIsPublishedClick}
                  label='Видно всем посетителям сайта'
                />
              </GridCol>
            )}
          </Grid>
        </>
      </Formik>
    </Dialog>
  )
}

export default memo(CommunicationForm)
